// Animated pictures (GIF, animated WebP, APNG): header sniffing and a frame-by-frame decoder.
//
// Based on src/video/anim-image.ts (the monitor's posters), which decodes every frame at the image's own size and
// keeps them all. Pictures can be larger (up to 800 px, 300 frames: 576 MB at full size), so here each composited
// frame goes to a callback as soon as it is decoded (the caller scales it down and keeps that) and only one frame
// at full size exists at a time. Paths:
//   1. ImageDecoder (WebCodecs) where the browser has it and supports the type (Chrome: GIF, WebP, APNG).
//   2. GIF: gifuct-js (loaded on demand), one frame decompressed at a time, patches composited here.
//   3. Animated WebP: the RIFF container is split here, each ANMF frame decoded as a still WebP and composited.
//   4. Anything else: null (the caller treats the file as a still image).
// The decoder yields to the event loop every YIELD_MS of work, so a long GIF does not block input.

export type AnimType = 'image/gif' | 'image/webp' | 'image/png';

export interface AnimInfo {
  type: AnimType;
  w: number;
  h: number;
  /** frame count (1 = still) */
  frames: number;
}

/** One composited frame (valid only during the call; draw it, do not keep it). */
export type FrameSink = (src: CanvasImageSource, index: number, delayMs: number) => void | boolean | Promise<void | boolean>;

export interface DecodeResult {
  w: number;
  h: number;
  /** frames passed to the sink */
  count: number;
  via: 'ImageDecoder' | 'gifuct' | 'webp';
}

const YIELD_MS = 8;

/** Browsers show GIF delays of 10 ms or less as 100 ms; same here. */
export const delayMs = (d: number) => (d <= 10 ? 100 : d);

const u16 = (b: Uint8Array, o: number) => b[o]! | (b[o + 1]! << 8);
const u24 = (b: Uint8Array, o: number) => b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16);
const u32 = (b: Uint8Array, o: number) => (b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16) | (b[o + 3]! << 24)) >>> 0;
const u32be = (b: Uint8Array, o: number) => ((b[o]! << 24) | (b[o + 1]! << 16) | (b[o + 2]! << 8) | b[o + 3]!) >>> 0;
const tag = (b: Uint8Array, o: number) => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);

/**
 * Type, size and frame count from the file bytes: GIF (image descriptors counted, as the server does), WebP (ANMF
 * chunks of a VP8X file with the animation flag), PNG (acTL chunk before the image data = APNG). Null for other types.
 */
export function sniffAnim(b: Uint8Array): AnimInfo | null {
  if (b.length >= 13 && tag(b, 0) === 'GIF8') return { type: 'image/gif', w: u16(b, 6), h: u16(b, 8), frames: gifFrameCount(b) };
  if (b.length >= 30 && tag(b, 0) === 'RIFF' && tag(b, 8) === 'WEBP') {
    if (tag(b, 12) !== 'VP8X') return { type: 'image/webp', w: 0, h: 0, frames: 1 };
    let frames = 0;
    if (b[20]! & 0x02) for (let o = 12; o + 8 <= b.length; ) { const n = u32(b, o + 4); if (tag(b, o) === 'ANMF') frames++; o += 8 + n + (n & 1); }
    return { type: 'image/webp', w: 1 + u24(b, 24), h: 1 + u24(b, 27), frames: Math.max(1, frames) };
  }
  if (b.length >= 33 && b[0] === 0x89 && tag(b, 1).startsWith('PNG')) {
    const w = u32be(b, 16), h = u32be(b, 20);
    for (let o = 8; o + 12 <= b.length; ) {
      const n = u32be(b, o), t = tag(b, o + 4);
      if (t === 'acTL') return { type: 'image/png', w, h, frames: Math.max(1, u32be(b, o + 8)) };
      if (t === 'IDAT') break;
      o += 12 + n;
    }
    return { type: 'image/png', w, h, frames: 1 };
  }
  return null;
}

/** Images in a GIF (the block walk of server/src/art.ts gifFrames; 0 = malformed). */
function gifFrameCount(b: Uint8Array): number {
  let o = 13;
  if (b[10]! & 0x80) o += 3 * (2 << (b[10]! & 7));
  const skip = () => { while (o < b.length) { const n = b[o]!; o += 1 + n; if (n === 0) return true; } return false; };
  let frames = 0;
  while (o < b.length) {
    const k = b[o]!;
    if (k === 0x3b) break;
    if (k === 0x21) { o += 2; if (!skip()) break; }
    else if (k === 0x2c) {
      if (o + 10 > b.length) break;
      const p = b[o + 9]!;
      o += 10;
      if (p & 0x80) o += 3 * (2 << (p & 7));
      o += 1;
      frames++;
      if (!skip()) break;
    } else return 0;
  }
  return frames;
}

/**
 * Decode every frame (up to `maxFrames`) and pass it to `sink` (composited, at the image's own size). The sink may
 * return false to stop. `signal` aborts between frames. Null when no decoder in this browser handles the file.
 */
export async function decodeFrames(buf: Uint8Array, sink: FrameSink, maxFrames: number, signal?: { aborted: boolean }): Promise<DecodeResult | null> {
  const info = sniffAnim(buf);
  if (!info) return null;
  try {
    const r = await withImageDecoder(buf, info.type, sink, maxFrames, signal);
    if (r) return r;
  } catch (e) {
    if (signal?.aborted) throw e;
    console.warn('[art] ImageDecoder failed, using the fallback decoder', e);
  }
  if (info.type === 'image/gif') return decodeGif(buf, sink, maxFrames, signal);
  if (info.type === 'image/webp' && info.frames > 1) return decodeWebp(buf, sink, maxFrames, signal);
  return null;
}

class Yielder {
  private t = performance.now();
  async maybe(signal?: { aborted: boolean }): Promise<void> {
    if (performance.now() - this.t > YIELD_MS) {
      await new Promise((r) => setTimeout(r, 0));
      this.t = performance.now();
    }
    if (signal?.aborted) throw new DOMException('aborted', 'AbortError');
  }
}

// ------------------------------------------------------------------ ImageDecoder (WebCodecs)
interface DecodedImage { image: VideoFrame }
interface ImageDecoderLike {
  completed: Promise<void>;
  tracks: { ready: Promise<void>; selectedTrack: { frameCount: number } | null };
  decode(o: { frameIndex: number }): Promise<DecodedImage>;
  close(): void;
}
interface ImageDecoderCtor {
  new (init: { data: BufferSource; type: string }): ImageDecoderLike;
  isTypeSupported(type: string): Promise<boolean>;
}

async function withImageDecoder(buf: Uint8Array, type: string, sink: FrameSink, maxFrames: number, signal?: { aborted: boolean }): Promise<DecodeResult | null> {
  const ID = (globalThis as unknown as { ImageDecoder?: ImageDecoderCtor }).ImageDecoder;
  if (!ID || !(await ID.isTypeSupported(type))) return null;
  const dec = new ID({ data: buf as Uint8Array<ArrayBuffer>, type });
  const y = new Yielder();
  try {
    await dec.tracks.ready;
    await dec.completed;
    const count = Math.min(maxFrames, dec.tracks.selectedTrack?.frameCount ?? 1);
    let w = 0, h = 0, n = 0;
    for (let i = 0; i < count; i++) {
      const { image } = await dec.decode({ frameIndex: i });
      try {
        w = image.displayWidth; h = image.displayHeight;
        n++;
        if ((await sink(image, i, delayMs((image.duration ?? 100_000) / 1000))) === false) break;
      } finally {
        image.close();
      }
      await y.maybe(signal);
    }
    return n ? { w, h, count: n, via: 'ImageDecoder' } : null;
  } finally {
    dec.close();
  }
}

// ------------------------------------------------------------------ GIF (gifuct-js, one frame at a time)
async function decodeGif(buf: Uint8Array, sink: FrameSink, maxFrames: number, signal?: { aborted: boolean }): Promise<DecodeResult | null> {
  const { parseGIF, decompressFrame } = await import('gifuct-js');
  const gif = parseGIF(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength) as ArrayBuffer);
  const w = gif.lsd.width, h = gif.lsd.height;
  if (!w || !h) return null;
  const c = canvas(w, h);
  const g = c.getContext('2d', { willReadFrequently: true })!;
  const patch = canvas(1, 1);
  const pg = patch.getContext('2d')!;
  const y = new Yielder();
  type F = ReturnType<typeof decompressFrame>;
  let prev: F | null = null;
  let saved: ImageData | null = null;
  let n = 0;
  try {
    for (const raw of gif.frames) {
      if (!('image' in raw)) continue;
      if (n >= maxFrames) break;
      const f = decompressFrame(raw, gif.gct, true);
      if (prev?.disposalType === 2) g.clearRect(prev.dims.left, prev.dims.top, prev.dims.width, prev.dims.height);
      else if (prev?.disposalType === 3 && saved) g.putImageData(saved, 0, 0);
      saved = f.disposalType === 3 ? g.getImageData(0, 0, w, h) : null;
      const { width, height, left, top } = f.dims;
      if (width > 0 && height > 0) {
        if (patch.width !== width || patch.height !== height) { patch.width = width; patch.height = height; }
        pg.putImageData(new ImageData(new Uint8ClampedArray(f.patch), width, height), 0, 0);
        g.drawImage(patch, left, top); // drawImage, not putImageData: transparent patch pixels keep what is below
      }
      const stop = (await sink(c, n, delayMs(f.delay))) === false;
      n++;
      prev = f;
      if (stop) break;
      await y.maybe(signal);
    }
  } finally {
    c.width = c.height = 0;
    patch.width = patch.height = 0;
  }
  return n ? { w, h, count: n, via: 'gifuct' } : null;
}

// ------------------------------------------------------------------ animated WebP (own demuxer)
interface Chunk { tag: string; data: Uint8Array }

function chunks(b: Uint8Array, start: number, end: number): Chunk[] {
  const out: Chunk[] = [];
  let o = start;
  while (o + 8 <= end) {
    const n = u32(b, o + 4);
    out.push({ tag: tag(b, o), data: b.subarray(o + 8, Math.min(end, o + 8 + n)) });
    o += 8 + n + (n & 1);
  }
  return out;
}

/** A still WebP file from one frame's chunks (ALPH + VP8, or VP8L, or VP8). */
function stillWebp(sub: Chunk[], w: number, h: number): Blob {
  const parts: Uint8Array[] = [];
  const put = (t: string, d: Uint8Array) => {
    const hd = new Uint8Array(8);
    for (let i = 0; i < 4; i++) hd[i] = t.charCodeAt(i);
    new DataView(hd.buffer).setUint32(4, d.length, true);
    parts.push(hd, d);
    if (d.length & 1) parts.push(new Uint8Array(1));
  };
  if (sub.some((c) => c.tag === 'ALPH')) {
    const x = new Uint8Array(10);
    x[0] = 0x10; // alpha flag
    x[4] = (w - 1) & 255; x[5] = ((w - 1) >> 8) & 255; x[6] = ((w - 1) >> 16) & 255;
    x[7] = (h - 1) & 255; x[8] = ((h - 1) >> 8) & 255; x[9] = ((h - 1) >> 16) & 255;
    put('VP8X', x);
  }
  for (const c of sub) if (c.tag === 'ALPH' || c.tag === 'VP8 ' || c.tag === 'VP8L') put(c.tag, c.data);
  const size = parts.reduce((a, p) => a + p.length, 4);
  const riff = new Uint8Array(12);
  riff.set([82, 73, 70, 70], 0); // RIFF
  new DataView(riff.buffer).setUint32(4, size, true);
  riff.set([87, 69, 66, 80], 8); // WEBP
  return new Blob([riff, ...parts] as BlobPart[], { type: 'image/webp' });
}

async function decodeWebp(b: Uint8Array, sink: FrameSink, maxFrames: number, signal?: { aborted: boolean }): Promise<DecodeResult | null> {
  const top = chunks(b, 12, b.length);
  const vp8x = top.find((c) => c.tag === 'VP8X')!.data;
  const w = u24(vp8x, 4) + 1, h = u24(vp8x, 7) + 1;
  const c = canvas(w, h);
  const g = c.getContext('2d')!;
  const y = new Yielder();
  let dispose: [number, number, number, number] | null = null;
  let n = 0;
  try {
    for (const a of top.filter((x) => x.tag === 'ANMF').slice(0, maxFrames)) {
      const d = a.data;
      const x = u24(d, 0) * 2, yy = u24(d, 3) * 2, fw = u24(d, 6) + 1, fh = u24(d, 9) + 1;
      const dur = u24(d, 12), flags = d[15]!;
      if (dispose) g.clearRect(...dispose);
      const bmp = await createImageBitmap(stillWebp(chunks(d, 16, d.length), fw, fh));
      if (flags & 0x02) g.clearRect(x, yy, fw, fh); // do not blend: the frame replaces its rectangle
      g.drawImage(bmp, x, yy);
      bmp.close();
      dispose = flags & 0x01 ? [x, yy, fw, fh] : null;
      const stop = (await sink(c, n, delayMs(dur))) === false;
      n++;
      if (stop) break;
      await y.maybe(signal);
    }
  } finally {
    c.width = c.height = 0;
  }
  return n ? { w, h, count: n, via: 'webp' } : null;
}

function canvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}
